/**
 * Fisher–Yates shuffle. Uses `crypto.getRandomValues` (rejection sampling, matching the unbiased-index technique
 * previously duplicated in `libs/cards/feature/src/lib/util/crypto-shuffle.ts` and `DicePromptComponent.rollDie`
 * in `@animus/ui/web`), falling back to `Math.random` when Web Crypto isn't available. Always returns a _new_ array
 * - the input is never mutated.
 */
export function cryptoShuffle<T>(items: readonly T[]): T[] {
    const result = [...items];

    for (let i = result.length - 1; i > 0; i--) {
        const j = randomIndex(i + 1);

        [result[i], result[j]] = [result[j], result[i]];
    }

    return result;
}

/**
 * Unbiased random integer in [0, exclusiveMax).
 */
function randomIndex(exclusiveMax: number): number {
    if (exclusiveMax <= 1) {
        return 0;
    }

    if (hasGetRandomValues()) {
        return cryptoRandomIndex(exclusiveMax);
    }

    return Math.floor(Math.random() * exclusiveMax);
}

function hasGetRandomValues(): boolean {
    return typeof globalThis.crypto?.getRandomValues === 'function';
}

/**
 * Unbiased random integer in [0, exclusiveMax) via `crypto.getRandomValues` rejection sampling.
 */
function cryptoRandomIndex(exclusiveMax: number): number {
    const buffer = new Uint32Array(1);
    const rejectionCeiling = Math.floor(0xff_ff_ff_ff / exclusiveMax) * exclusiveMax;
    let value: number;

    do {
        globalThis.crypto.getRandomValues(buffer);
        value = buffer[0];
    } while (value >= rejectionCeiling);

    return value % exclusiveMax;
}
